import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { IconButton } from "./Button";
import { AlertIcon, CheckCircleIcon, CloseIcon } from "./icons";

/* ==========================================================================
   The transient channel.

   Before this, "saved", "work started" and "signed out" were all rendered as
   persistent Banners that a person had to dismiss by hand — a banner doing a
   toast's job. A Banner is for a condition that is still true; a Toast is for
   something that just happened.
   ========================================================================== */

export interface ToastOptions {
  tone?: "success" | "error";
  /** Milliseconds. Errors linger; successes get out of the way. */
  duration?: number;
}

interface ToastEntry {
  id: number;
  message: string;
  tone: "success" | "error";
  duration: number;
}

interface ToastApi {
  show: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const arm = useCallback(
    (id: number, ms: number) => {
      timers.current.set(
        id,
        window.setTimeout(() => dismiss(id), ms),
      );
    },
    [dismiss],
  );

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const tone = options.tone ?? "success";
      const duration = options.duration ?? (tone === "error" ? 8000 : 4000);
      const id = nextId.current++;
      setToasts((current) => [...current, { id, message, tone, duration }]);
      arm(id, duration);
    },
    [arm],
  );

  // A toast someone is reading (hovering or focused, e.g. tabbed to its
  // dismiss button) should not vanish under them mid-read.
  const hold = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id));
  }, []);

  const release = useCallback(
    (id: number) => {
      const toast = toasts.find((entry) => entry.id === id);
      if (toast) arm(id, toast.duration);
    },
    [arm, toasts],
  );

  useEffect(
    () => () => {
      timers.current.forEach((timerId) => window.clearTimeout(timerId));
    },
    [],
  );

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Always in the DOM: a live region added at the same moment as its first
          message is frequently not announced at all. */}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast${toast.tone === "error" ? " toast--error" : ""}`}
            // An error is content a person may need to re-read; it interrupts
            // instead of waiting for the reader to fall idle.
            role={toast.tone === "error" ? "alert" : undefined}
            onMouseEnter={() => hold(toast.id)}
            onMouseLeave={() => release(toast.id)}
            onFocusCapture={() => hold(toast.id)}
            onBlurCapture={() => release(toast.id)}
          >
            {toast.tone === "error" ? <AlertIcon /> : <CheckCircleIcon />}
            <span>{toast.message}</span>
            <IconButton label="Dismiss" className="icon-btn--sm" onClick={() => dismiss(toast.id)}>
              <CloseIcon />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Returns a no-op outside a provider rather than throwing: a missing provider
 * should cost a missing confirmation message, never a blank screen.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  return api ?? NO_TOASTS;
}

const NO_TOASTS: ToastApi = { show: () => undefined };
